/**
 * Convert a wall-clock time expressed in an IANA timezone (e.g. America/Chicago)
 * to the corresponding UTC `Date`, independent of the host environment's
 * local timezone. Uses only `Intl.DateTimeFormat`, so no runtime dependency
 * is added and the IANA tzdata shipped by the platform is the source of
 * truth for DST.
 *
 * Algorithm (iterative guess-and-refine):
 *   1. Treat the input wall-clock as if it were UTC to form a candidate `guess`.
 *   2. Read the zone's offset at the current candidate and refine the
 *      candidate to `utc = guess - offset`. The offset can change across a
 *      DST boundary, so we re-read the offset at the refined `utc` and
 *      refine again until the offset is stable (at most a few iterations).
 *   3. Re-format the stable `utc` in the zone and verify the wall-clock
 *      round-trips. If it doesn't, the original wall-clock lies in a DST
 *      gap (e.g. 02:30 on the US spring-forward morning) and there is no
 *      real UTC instant for it; a `TimezoneConversionError` with `code: 'gap'`
 *      is thrown.
 *   4. Detect fall-back ambiguity by probing for a second UTC instant that
 *      also produces the requested wall-clock in the target zone. If one
 *      exists, the input is in the fall-back overlap; the later UTC instant
 *      is returned regardless of whether the iterative refinement landed
 *      on the pre- or post-transition occurrence, so zones with non-hourly
 *      offsets (e.g. Australia/Lord_Howe) and forward-falling-back zones
 *      (e.g. Europe/Berlin) produce the same outcome.
 *
 * Ambiguity policy (post-transition / "later UTC"):
 *   When the requested wall-clock occurs twice during a fall-back, this
 *   helper resolves it to the LATER UTC instant — i.e. the post-transition
 *   occurrence, after the offset has shifted back to standard time. This
 *   matches the documented newsletter-scheduling requirement and is
 *   deterministic across browsers and runtimes because it depends only on
 *   `Intl.DateTimeFormat` against the platform's IANA tzdata. Callers that
 *   need to surface the policy to an operator can read the `ambiguous`
 *   flag and the `preTransitionUtc` companion instant from
 *   `formatZonedTimePreview`.
 */
export interface ZonedWallClock {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

export type TimezoneErrorCode = 'invalid' | 'gap' | 'unknown-zone'

export class TimezoneConversionError extends Error {
  readonly code: TimezoneErrorCode
  constructor(message: string, code: TimezoneErrorCode) {
    super(message)
    this.name = 'TimezoneConversionError'
    this.code = code
  }
}

const MS_PER_MINUTE = 60_000

/** Window (in milliseconds) we probe on either side of the candidate to
 *  detect an alternate occurrence for the requested wall-clock. 4 hours
 *  covers every current and historical DST offset published in IANA tzdata. */
const AMBIGUITY_PROBE_WINDOW_MS = 4 * 60 * 60 * 1000
/** Step (in minutes) used while probing for an alternate occurrence. Smaller
 *  steps raise precision for sub-hour offsets but stay cheap because the
 *  probe only runs when the candidate round-trips successfully. */
const AMBIGUITY_PROBE_STEP_MS = 5 * 60 * 1000

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function isValidParts(p: ZonedWallClock): boolean {
  if (!Number.isInteger(p.year) || p.year < 1970 || p.year > 9999) return false
  if (!Number.isInteger(p.month) || p.month < 1 || p.month > 12) return false
  if (!Number.isInteger(p.day) || p.day < 1 || p.day > 31) return false
  if (!Number.isInteger(p.hour) || p.hour < 0 || p.hour > 23) return false
  if (!Number.isInteger(p.minute) || p.minute < 0 || p.minute > 59) return false
  return true
}

/**
 * Read the timezone's offset in minutes at a given UTC instant.
 * Uses the difference between the wall-clock formatted in the target zone
 * and the same instant formatted in UTC, so the result is exact even for
 * historical zones with non-zero seconds.
 */
function getZoneOffsetMs(utc: Date, timeZone: string): number {
  const localMs = zonedDateAsUtcMs(utc, timeZone)
  const utcMs = zonedDateAsUtcMs(utc, 'UTC')
  return localMs - utcMs
}

function zonedDateAsUtcMs(utc: Date, timeZone: string): number {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  const parts = fmt.formatToParts(utc)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
  let hour = get('hour')
  if (hour === 24) hour = 0
  return Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second'),
  )
}

function getZoneWallClock(utc: Date, timeZone: string): ZonedWallClock {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const parts = fmt.formatToParts(utc)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
  let hour = get('hour')
  if (hour === 24) hour = 0
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour,
    minute: get('minute'),
  }
}

function wallClockEquals(a: ZonedWallClock, b: ZonedWallClock): boolean {
  return (
    a.year === b.year &&
    a.month === b.month &&
    a.day === b.day &&
    a.hour === b.hour &&
    a.minute === b.minute
  )
}

interface ResolveOptions {
  /** Length of the fall-back overlap probe, in milliseconds. Overridable for tests. */
  ambiguityProbeWindowMs?: number
  /** Step (ms) used while scanning for an alternate occurrence. Overridable for tests. */
  ambiguityProbeStepMs?: number
}

interface ResolvedZonedTime {
  utc: Date
  ambiguous: boolean
  preTransitionUtc: Date | null
}

/**
 * Convert `parts` (a wall-clock in `timeZone`) to a UTC `Date`. When the
 * wall-clock sits inside the fall-back overlap, the later (post-transition)
 * UTC instant is returned and `ambiguous` is true. Spring-forward gaps
 * still raise a `gap` error.
 *
 * The function is exported primarily for testing — application code should
 * use `formatZonedTimePreview`, which surfaces the ambiguity signal.
 */
export function resolveZonedTime(
  parts: ZonedWallClock,
  timeZone: string,
  options: ResolveOptions = {},
): ResolvedZonedTime {
  if (!isValidParts(parts)) {
    throw new TimezoneConversionError('Enter a valid date and time.', 'invalid')
  }
  const probe = new Date(parts.year, parts.month - 1, parts.day)
  if (
    probe.getFullYear() !== parts.year ||
    probe.getMonth() + 1 !== parts.month ||
    probe.getDate() !== parts.day
  ) {
    throw new TimezoneConversionError('That day does not exist in the selected month.', 'invalid')
  }

  const probeWindowMs = options.ambiguityProbeWindowMs ?? AMBIGUITY_PROBE_WINDOW_MS
  const probeStepMs = options.ambiguityProbeStepMs ?? AMBIGUITY_PROBE_STEP_MS

  const guess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
  )
  let utc = new Date(guess)
  let prevOffset = Number.NaN
  for (let i = 0; i < 4; i++) {
    const offsetMs = getZoneOffsetMs(utc, timeZone)
    if (offsetMs === prevOffset) break
    prevOffset = offsetMs
    utc = new Date(guess - offsetMs)
  }

  const back = getZoneWallClock(utc, timeZone)
  if (!wallClockEquals(back, parts)) {
    throw new TimezoneConversionError(
      `That time does not exist in ${timeZone} (DST gap). Pick a different time.`,
      'gap',
    )
  }

  // Fall-back ambiguity check: scan both directions of the candidate for a
  // second UTC instant that produces the requested wall-clock. We probe on
  // either side because:
  //   * Whole-hour zones (America/Chicago) have the alternate occurrence
  //     exactly one hour later than the refinement result.
  //   * Zones where the iterative refinement already converges on the
  //     post-transition side (Europe/Berlin) have the alternate occurrence
  //     one hour earlier.
  //   * Sub-hour zones (Australia/Lord_Howe) need the configurable step.
  // Stepping in 5-minute increments across a ±4-hour window keeps every
  // IANA tzdata offset within reach at a worst case of ~96 formatToParts
  // calls per conversion.
  let alternateUtc: Date | null = null
  for (let delta = probeStepMs; delta <= probeWindowMs; delta += probeStepMs) {
    const candidateEarlier = new Date(utc.getTime() - delta)
    if (wallClockEquals(getZoneWallClock(candidateEarlier, timeZone), parts)) {
      alternateUtc = candidateEarlier
      break
    }
    const candidateLater = new Date(utc.getTime() + delta)
    if (wallClockEquals(getZoneWallClock(candidateLater, timeZone), parts)) {
      alternateUtc = candidateLater
      break
    }
  }

  if (!alternateUtc) {
    return { utc, ambiguous: false, preTransitionUtc: null }
  }

  // Apply the post-transition policy by returning whichever of the two
  // candidates is later in UTC.
  if (alternateUtc.getTime() > utc.getTime()) {
    return { utc: alternateUtc, ambiguous: true, preTransitionUtc: utc }
  }
  return { utc, ambiguous: true, preTransitionUtc: alternateUtc }
}

export interface ZonedTimePreview {
  firesAtUtc: Date
  firesAtLocal: string
  zoneAbbreviation: string
  utcString: string
  offsetMinutes: number
  /** True when the requested wall-clock occurs twice during a fall-back and the helper resolved to the post-transition occurrence. */
  ambiguous: boolean
  /** The pre-transition UTC instant for the same wall-clock, when `ambiguous` is true; otherwise null. */
  preTransitionUtc: Date | null
  /** Human-readable description of which occurrence was chosen (e.g. "post-transition standard time"). */
  ambiguityNote: string | null
}

/**
 * Convert a wall-clock time expressed in `timeZone` (an IANA name) to the
 * matching UTC `Date`. Thin convenience wrapper around `resolveZonedTime`
 * that returns only the resolved instant — call sites that need to surface
 * the ambiguity policy to an operator should use `formatZonedTimePreview`.
 */
export function zonedTimeToUtc(parts: ZonedWallClock, timeZone: string): Date {
  return resolveZonedTime(parts, timeZone).utc
}

export function formatZonedTimePreview(
  parts: ZonedWallClock,
  timeZone: string,
): ZonedTimePreview {
  const resolved = resolveZonedTime(parts, timeZone)
  const utc = resolved.utc
  const human = new Intl.DateTimeFormat(undefined, {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  })
  const humanParts = human.formatToParts(utc)
  const zoneAbbreviation = humanParts.find((p) => p.type === 'timeZoneName')?.value ?? ''
  const firesAtLocal = human.format(utc)
  const offsetMinutes = Math.round(getZoneOffsetMs(utc, timeZone) / MS_PER_MINUTE)
  return {
    firesAtUtc: utc,
    firesAtLocal,
    zoneAbbreviation,
    offsetMinutes,
    ambiguous: resolved.ambiguous,
    preTransitionUtc: resolved.preTransitionUtc,
    ambiguityNote: resolved.ambiguous
      ? `This wall-clock occurs twice during the fall-back transition; the campaign will fire at the post-transition (${zoneAbbreviation}) occurrence.`
      : null,
    utcString: `${utc.getUTCFullYear()}-${pad2(utc.getUTCMonth() + 1)}-${pad2(utc.getUTCDate())} ${pad2(utc.getUTCHours())}:${pad2(utc.getUTCMinutes())} UTC`,
  }
}

/** Format a UTC instant as the `YYYY-MM-DDTHH:mm` value a `<input type="datetime-local">` expects. */
export function formatWallClockInZone(utc: Date, timeZone: string): string {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const parts = fmt.formatToParts(utc)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00'
  let hour = get('hour')
  if (hour === '24') hour = '00'
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`
}

/** Parse a `YYYY-MM-DDTHH:mm` (or `YYYY-MM-DDTHH:mm:ss`) datetime-local value. */
export function parseDateTimeLocal(value: string): ZonedWallClock | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/)
  if (!m) return null
  const [, y, mo, d, h, mi] = m
  return {
    year: Number(y),
    month: Number(mo),
    day: Number(d),
    hour: Number(h),
    minute: Number(mi),
  }
}

/** Build a `+-HH:MM` offset string for display. */
export function formatOffsetLabel(offsetMinutes: number): string {
  const sign = offsetMinutes >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMinutes)
  const oh = Math.floor(abs / 60)
  const om = abs % 60
  return `UTC${sign}${pad2(oh)}:${pad2(om)}`
}